// Brand Controls redesign — Version History tab.
//
// Rows bound to VersionHistoryItem { version, date, changes, usedInFixes,
// status }. Active row = status === "current" (ivory card + teal badge,
// per the selection pattern — never a teal-tinted row). There is no author
// field in the data, so the sub-line shows date + usage, not an author.

import type { VersionHistoryItem } from "../../../services/brand/brand-voice-service";

export interface HistoryViewProps {
  versions: VersionHistoryItem[];
  onView: (version: string) => void;
  onRollback: (version: string) => void;
}

export function HistoryView({ versions, onView, onRollback }: HistoryViewProps) {
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--foreground)", margin: "0 0 3px" }}>Version history</h2>
        <p style={{ fontSize: 13.5, color: "var(--muted-foreground)", margin: 0 }}>
          Every saved profile. View any version or roll back to make it active.
        </p>
      </div>
      <div style={{ border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--elevation-sm)" }}>
        {versions.map((v, i) => {
          const active = v.status === "current";
          const usage = typeof v.usedInFixes === "number" ? v.usedInFixes : 0;
          return (
            <div
              key={v.version}
              className="bc-row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "15px 18px",
                borderBottom: i < versions.length - 1 ? "1px solid var(--border)" : "none",
                background: active ? "var(--alpha-teal-5)" : "var(--card)",
              }}
            >
              <span
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700,
                  background: active ? "var(--alpha-teal-10)" : "var(--muted)",
                  color: active ? "var(--teal-text)" : "var(--muted-foreground)",
                  border: `1px solid ${active ? "var(--alpha-teal-20)" : "var(--border)"}`,
                }}
              >
                v{v.version}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--foreground)" }}>{v.changes}</span>
                  {active && (
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--success-text)", background: "var(--success-bg)", border: "1px solid var(--success-border)", borderRadius: 9999, padding: "2px 8px" }}>
                      Active
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--muted-foreground)", marginTop: 2 }}>
                  {v.date} · used in {usage} {usage === 1 ? "asset" : "assets"}
                </div>
              </div>
              <button
                onClick={() => onView(v.version)}
                style={{ fontSize: 12.5, fontWeight: 600, color: "var(--foreground)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 13px", cursor: "pointer", flexShrink: 0 }}
              >
                View
              </button>
              {!active && (
                <button
                  onClick={() => onRollback(v.version)}
                  style={{ fontSize: 12.5, fontWeight: 600, color: "var(--teal-text)", background: "var(--teal-pill-bg)", border: "1px solid var(--alpha-teal-20)", borderRadius: 8, padding: "6px 13px", cursor: "pointer", flexShrink: 0 }}
                >
                  Make active
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
